/**
 * Parses pasted spreadsheet text into an HTML table.
 * Handles: newlines, || row breaks, tabs/pipes/semicolons, Shopify-collapsed single-line pastes.
 */
(function () {
  const SIZE_SUFFIXES = ['2XL', '3XL', '4XL', '5XL', 'XXL', 'XL', 'XS', '3X', '4X', '5X', 'XXS', 'SM', 'MD', 'LG'];
  const SIZE_TOKENS = new Set([
    '2XL', '3XL', '4XL', '5XL', 'XXL', 'XL', 'XS', '3X', '4X', '5X', 'XXS', 'SM', 'MD', 'LG',
    '1', '2', '3', '4', '5', '6', 'S', 'M', 'L'
  ]);
  const DIGIT_RE = /\d/;

  function extractRichTextNode(node) {
    if (!node) return '';
    if (node.type === 'text') return node.value || '';
    if (node.type === 'paragraph') {
      return (node.children || []).map(extractRichTextNode).join('');
    }
    if (node.type === 'root' || node.type === 'list') {
      return (node.children || []).map(extractRichTextNode).join('\n');
    }
    if (node.children) return node.children.map(extractRichTextNode).join('');
    return '';
  }

  function extractPlainText(raw) {
    if (typeof raw === 'object' && raw !== null) {
      if (raw.type === 'root') return extractRichTextNode(raw).trim();
      return '';
    }
    if (typeof raw !== 'string') return String(raw || '').trim();
    const trimmed = raw.trim();
    if (trimmed.startsWith('{') && trimmed.includes('"type"')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (parsed.type === 'root') return extractRichTextNode(parsed).trim();
      } catch (e) {
        /* use raw string */
      }
    }

    // Shopify can stringify rich text drops as Ruby-style hashes in Liquid:
    // {"type"=>"root", ... "value"=>"Size\tWidth\nS\t20"}
    if (trimmed.includes('"value"=>')) {
      const valueMatch = trimmed.match(/"value"=>"([\s\S]*?)"\}\]?\}?/);
      if (valueMatch && valueMatch[1]) {
        return valueMatch[1]
          .replace(/\\"/g, '"')
          .replace(/\\t/g, '\t')
          .replace(/\\n/g, '\n');
      }
    }

    return raw.replace(/\\t/g, '\t').replace(/\\n/g, '\n');
  }

  function stripHtml(text) {
    const el = document.createElement('div');
    el.innerHTML = text;
    return (el.textContent || '').trim();
  }

  function detectColDelim(raw) {
    if (raw.includes('\t')) return '\t';
    if (raw.includes('||')) return '\t';
    if (raw.includes('|')) return '|';
    if (raw.includes(';')) return ';';
    return '\t';
  }

  function splitCell(cell, colDelim) {
    return cell.split(colDelim).map((c) => c.trim()).filter(Boolean);
  }

  function expandMergedCell(cell) {
    const c = cell.trim();
    if (!c) return [];

    for (const suffix of SIZE_SUFFIXES) {
      if (c.length > suffix.length && c.endsWith(suffix)) {
        const prefix = c.slice(0, -suffix.length);
        if (prefix) return [prefix, suffix];
      }
    }

    for (const suffix of ['S', 'M', 'L']) {
      if (c.length > suffix.length && c.endsWith(suffix)) {
        const prefix = c.slice(0, -suffix.length);
        if (prefix && DIGIT_RE.test(prefix[prefix.length - 1])) {
          return [prefix, suffix];
        }
      }
    }

    return [c];
  }

  function flattenRowCells(cells) {
    const out = [];
    for (const cell of cells) {
      out.push(...expandMergedCell(cell));
    }
    return out;
  }

  function detectHeaderCols(cells) {
    for (let idx = 2; idx < cells.length; idx++) {
      const token = cells[idx].trim().toUpperCase();
      if (!SIZE_TOKENS.has(token)) continue;
      const next = cells[idx + 1];
      if (next && DIGIT_RE.test(next)) return idx;
    }
    return 0;
  }

  function rebuildLinesFromFlat(raw, colDelim) {
    const parts = raw.split(colDelim).map((p) => p.trim()).filter(Boolean);
    if (parts.length <= 5) return null;

    const cells = flattenRowCells(parts);
    const headerCols = detectHeaderCols(cells);
    if (headerCols < 1) return null;

    const lines = [];
    lines.push(cells.slice(0, headerCols).join(colDelim));

    for (let i = headerCols; i < cells.length; i += headerCols) {
      const row = cells.slice(i, i + headerCols);
      if (row.length) lines.push(row.join(colDelim));
    }
    return lines;
  }

  function parseLines(raw) {
    const text = stripHtml(raw).replace(/\r/g, '').replace(/\\n/g, '\n');
    if (!text) return [];

    let lines;
    if (text.includes('||')) {
      lines = text.split('||').map((l) => l.trim()).filter(Boolean);
    } else {
      lines = text.split('\n').map((l) => l.trim()).filter(Boolean);
    }

    if (lines.length === 1) {
      const colDelim = detectColDelim(text);
      const rebuilt = rebuildLinesFromFlat(lines[0], colDelim);
      if (rebuilt && rebuilt.length > 1) lines = rebuilt;
    }

    return lines;
  }

  function compactRow(cells) {
    return cells.map((c) => c.trim()).filter(Boolean);
  }

  function isTitleOnlyRow(cells) {
    if (cells.length !== 1) return false;
    const c = cells[0];
    return c.length > 15 && !['size', 'suay', 'pom'].includes(c.toLowerCase());
  }

  function isLikelyTitleHeaderRow(cells) {
    const first = cells[0] || '';
    const firstLower = first.toLowerCase();
    if (first.length <= 15) return false;
    if (['size', 'suay', 'pom'].includes(firstLower)) return false;
    return true;
  }

  function headerCellsFromRow(cells) {
    if (!isLikelyTitleHeaderRow(cells)) return cells;
    const rest = compactRow(cells.slice(1));
    return rest.length >= 2 ? rest : null;
  }

  function parseChartData(raw) {
    const text = extractPlainText(raw);
    const colDelim = detectColDelim(stripHtml(text));
    const lines = parseLines(text);
    const rows = [];

    for (const line of lines) {
      const cells = compactRow(line.split(colDelim));
      if (cells.length < 2) continue;
      if (isTitleOnlyRow(cells)) continue;

      if (rows.length === 0) {
        const headerCells = headerCellsFromRow(cells);
        if (!headerCells) continue;
        rows.push({ type: 'header', cells: headerCells });
      } else {
        rows.push({ type: 'data', cells });
      }
    }

    return rows;
  }

  function buildTable(rows) {
    if (!rows.length) return null;

    const header = rows.find((r) => r.type === 'header');
    if (!header) return null;

    let headerCells = header.cells.slice();
    const firstData = rows.find((r) => r.type === 'data');
    if (firstData && firstData.cells.length > headerCells.length) {
      const lead = firstData.cells[0].trim().toUpperCase();
      if (SIZE_TOKENS.has(lead) && headerCells[0]?.toUpperCase() !== 'SIZE') {
        headerCells.unshift('Size');
      }
    }

    const colCount = headerCells.length;
    const table = document.createElement('table');
    const thead = document.createElement('thead');
    const headTr = document.createElement('tr');

    for (const cell of headerCells) {
      const th = document.createElement('th');
      th.textContent = cell;
      headTr.appendChild(th);
    }
    thead.appendChild(headTr);
    table.appendChild(thead);

    const tbody = document.createElement('tbody');
    for (const row of rows) {
      if (row.type !== 'data') continue;
      const tr = document.createElement('tr');
      for (let i = 0; i < colCount; i++) {
        const td = document.createElement('td');
        td.textContent = row.cells[i] || '';
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    return table;
  }

  function mount(container) {
    if (container.querySelector('table')) return;

    const source = container.querySelector('.size-chart-data');
    if (!source) return;

    let raw = source.tagName === 'TEXTAREA' ? source.value : source.textContent.trim();
    raw = extractPlainText(raw);
    if (!raw) return;

    const rows = parseChartData(raw);
    const table = buildTable(rows);
    if (!table) return;

    source.remove();
    container.appendChild(table);
  }

  function init() {
    document.querySelectorAll('[data-size-chart-table]').forEach(mount);
  }

  window.SuaySizeChart = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
